import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import moment from "moment";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  ProductsReportsFilterKeys,
  ProductsReportsListFilterOpts
} from "../../components/ProductsReportsListPage/filters";
import {
  InitialProductsReportsFilterData_brands_edges_node,
  InitialProductsReportsFilterData_malls_edges_node
} from "../../types/InitialProductsReportsFilterData";
import {
  ProductsReportsListUrlFilters,
  ProductsReportsListUrlFiltersEnum,
  ProductsReportsListUrlFiltersWithMultipleValues,
  ProductsReportsUrlQueryParams
} from "../../urls";
export const ProductsReports_FILTERS_KEY = "ProductsReportsFilters";

export function getFilterOpts(
  params: ProductsReportsListUrlFilters,
  brands: {
    initial: InitialProductsReportsFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialProductsReportsFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): ProductsReportsListFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },

    startDate: {
      active: maybe(
        () =>
          [params.orderDateFrom, params.orderDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderDateTo, ""),
        min: maybe(() => params.orderDateFrom, "")
      }
    }
  };
}

export function getFilterVariables(params: ProductsReportsListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );
  const endDate = moment(`${endYear}-${endMonth}-${endDay} 00:00:00`)
    .add(1, "days")
    .format("YYYY-MM-DD HH:mm:ssZZ");

  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),

    orderDate: getGteLteVariables({
      gte: params.orderDateFrom
        ? moment(`${params.orderDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),
      lte: params.orderDateTo
        ? moment(`${params.orderDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : endDate
    }),
    search: params.query
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<ProductsReportsFilterKeys>,
  params: ProductsReportsListUrlFilters
): ProductsReportsListUrlFilters {
  const { active, group, name, value } = filter;

  if (!!group) {
    const rest = params && params[group] ? params[group] : undefined;

    return {
      [group]: active
        ? {
            ...(rest === undefined ? {} : rest),
            [name]: value
          }
        : rest
    };
  }

  switch (name) {
    case ProductsReportsFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        ProductsReportsListUrlFiltersWithMultipleValues.mall
      );
    case ProductsReportsFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        ProductsReportsListUrlFiltersWithMultipleValues.brand
      );

    case ProductsReportsFilterKeys.startDate:
      return getMinMaxQueryParam(
        filter,
        ProductsReportsListUrlFiltersEnum.orderDateFrom,
        ProductsReportsListUrlFiltersEnum.orderDateTo
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<ProductsReportsListUrlFilters>(
  ProductsReports_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  ProductsReportsUrlQueryParams,
  ProductsReportsListUrlFilters
>({
  ...ProductsReportsListUrlFiltersEnum,
  ...ProductsReportsListUrlFiltersWithMultipleValues
});
